package com.evoapps.evoshift.domain.rosterimport

import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.ShiftTemplate

/** SH-217: what a code on the sheet ("R", "W", "L4", "UW", …) stands for. */
sealed interface RosterCodeAction {
    /** A shift with these times. */
    data class Shift(val times: ClockRange) : RosterCodeAction

    /** A shift as the workplace's template [templateId] (its times, position and skills). */
    data class Template(val templateId: String) : RosterCodeAction

    /** A day off: nothing is created. */
    data object DayOff : RosterCodeAction

    /**
     * An absence of [type]. The import does not create absences (the app has no way for a manager
     * to enter one for someone else): such days stay free and are listed.
     */
    data class Absence(val type: AbsenceType) : RosterCodeAction

    /** Not part of the roster (a note, a total): left out. */
    data object Ignore : RosterCodeAction
}

/**
 * SH-217: the workplace's code dictionary (`workplaces/{id}/roster_import/codes`, owners and
 * managers only), saved with each import and filled in again the next time. Codes are compared
 * as [RosterCodes.normalize] writes them.
 */
data class RosterCodeDictionary(val codes: Map<String, RosterCodeAction> = emptyMap()) {
    operator fun get(code: String): RosterCodeAction? = codes[RosterCodes.normalize(code)]

    /** [code] stands for [action] (null: forgotten). */
    fun with(code: String, action: RosterCodeAction?): RosterCodeDictionary {
        val key = RosterCodes.normalize(code)
        return copy(codes = if (action == null) codes - key else codes + (key to action))
    }

    /** Adds [newer]'s codes, which win; what this one has and [newer] does not stays. */
    fun mergedWith(newer: RosterCodeDictionary): RosterCodeDictionary = copy(codes = codes + newer.codes)

    companion object {
        /** The most codes kept (firestore.rules allow as many). */
        const val MAX_CODES = 300
        /** The longest code kept. */
        const val MAX_CODE_LENGTH = 40
    }
}

object RosterCodes {
    /** The form codes are compared in: trimmed, one space between words, upper case ("l4" = "L4"). */
    fun normalize(code: String): String =
        code.trim().split(' ', '\t', '\n', '\r').filter { it.isNotEmpty() }.joinToString(" ").uppercase()

    /**
     * What the screen suggests for a code met for the first time (the person confirms or changes
     * it): the Polish absence codes everyone uses ("L4" sick leave, "UW" vacation, "UŻ" leave on
     * demand), "W"/"WOLNE"/"X"/"-" as a day off, a template's name as that template. Null: no
     * suggestion.
     */
    fun suggestion(code: String, templates: List<ShiftTemplate>): RosterCodeAction? {
        val key = normalize(code)
        templates.firstOrNull { it.name.isNotBlank() && normalize(it.name) == key }?.let { return RosterCodeAction.Template(it.id) }
        return when (key) {
            "L4", "L-4", "ZW", "ZWOLNIENIE", "CHOROBOWE" -> RosterCodeAction.Absence(AbsenceType.SICK_LEAVE)
            "UW", "URLOP", "UL", "U" -> RosterCodeAction.Absence(AbsenceType.VACATION)
            "UŻ", "UZ", "NŻ" -> RosterCodeAction.Absence(AbsenceType.ON_DEMAND)
            "W", "WOLNE", "WOLNY", "X", "-", "–", "—", "DW", "OFF" -> RosterCodeAction.DayOff
            else -> null
        }
    }
}
